'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
const dayMs = 86_400_000;
const hourMs = 3_600_000;
const minuteMs = 60_000;
/**
 * Hook that manages countdown timer functionality from a begin date to a target date.
 *
 * @param options - Countdown options or target date string/Date
 * @param options.targetDateTime - The date/time when the countdown should end
 * @param options.beginDateTime - The date/time when the countdown should begin. Defaults to 59 minutes 59 seconds before target
 * @param options.intervalInSecs - The interval in seconds to update the countdown. Defaults to 1 second
 * @returns A formatted countdown string (e.g., "5 timmar", "30 minuter", "10 sekunder") or undefined if not counting down
 */
export function useCountdown(t0) {
  const $ = _c(12);
  const {
    targetDateTime,
    beginDateTime,
    intervalInSecs: t1
  } = t0;
  const intervalInSecs = t1 === undefined ? 1 : t1;
  const wasCountingDown = useRef(false);
  const timerRef = useRef(undefined);
  const [countdown, setCountdown] = useState(undefined);
  const prevCountdownRef = useRef(undefined);
  let t2;
  let targetDateTimeValue;
  if ($[0] !== beginDateTime || $[1] !== targetDateTime) {
    const targetDate = new Date(targetDateTime);
    targetDateTimeValue = targetDate.getTime();
    const beginDate = beginDateTime ? new Date(beginDateTime) : new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), targetDate.getHours() - 1, targetDate.getMinutes(), 0, 0);
    t2 = beginDate.getTime();
    $[0] = beginDateTime;
    $[1] = targetDateTime;
    $[2] = t2;
    $[3] = targetDateTimeValue;
  } else {
    t2 = $[2];
    targetDateTimeValue = $[3];
  }
  const beginDateTimeValue = t2;
  let t3;
  if ($[4] !== beginDateTimeValue || $[5] !== targetDateTimeValue) {
    t3 = () => {
      const currentTimeValue = Date.now();
      if (currentTimeValue >= beginDateTimeValue && currentTimeValue <= targetDateTimeValue) {
        wasCountingDown.current = true;
        const distance = targetDateTimeValue - currentTimeValue;
        const days = Math.floor(distance / dayMs);
        const hours = Math.floor(distance % dayMs / hourMs);
        const minutes = Math.floor(distance % hourMs / minuteMs);
        const seconds = Math.floor(distance % minuteMs / 1000);
        let newCountdown = undefined;
        if (days) {
          newCountdown = `${days} dag${days > 1 ? "ar" : ""}`;
        } else {
          if (hours) {
            newCountdown = `${hours + 1} timmar`;
          } else {
            if (minutes) {
              newCountdown = `${minutes + 1} minuter`;
            } else {
              if (seconds) {
                newCountdown = `${seconds} sekund${seconds > 1 ? "er" : ""}`;
              }
            }
          }
        }
        if (prevCountdownRef.current !== newCountdown) {
          setCountdown(newCountdown);
        }
        prevCountdownRef.current = newCountdown;
      } else {
        setCountdown(undefined);
        if (timerRef.current !== undefined) {
          clearInterval(timerRef.current);
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      }
    };
    $[4] = beginDateTimeValue;
    $[5] = targetDateTimeValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const calculateCountdown = t3;
  let t4;
  let t5;
  if ($[7] !== beginDateTimeValue || $[8] !== calculateCountdown || $[9] !== intervalInSecs) {
    t4 = () => {
      setCountdown(undefined);
      const initializeCountdown = () => {
        calculateCountdown();
        timerRef.current = setInterval(calculateCountdown, intervalInSecs * 1000);
      };
      const now = Date.now();
      if (now < beginDateTimeValue) {
        const delay = beginDateTimeValue - now;
        timerRef.current = setTimeout(initializeCountdown, delay);
      } else {
        initializeCountdown();
      }
      return () => {
        if (timerRef.current !== undefined) {
          clearInterval(timerRef.current);
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      };
    };
    t5 = [beginDateTimeValue, calculateCountdown, intervalInSecs];
    $[7] = beginDateTimeValue;
    $[8] = calculateCountdown;
    $[9] = intervalInSecs;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  return countdown;
}